import { ProductLabel } from "../../ui/product-label.tsx";
import { ChannelName } from "../../ui/channel.tsx";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { useEffect, useRef, useState } from "react";
import { api, q } from "../../api.ts";
import { number } from "../../format.ts";
import { INBOX_LABELS as I, READING, readingLabel, reasonText } from "../../labels.ts";
import { MetricSummary } from "../../metrics.tsx";
import { Button, ErrorText, Kbd, Pill, Reasons } from "../../ui.tsx";
import { Excerpt, PointList } from "../../ui/review.tsx";
import { Skeleton } from "../../ui/skeleton.tsx";
import { useInbox, Review, DetailPanelShell } from "./shared.tsx";

// Part of the inbox (docs/design-system.md §0.4, plan M7 step 7g).

export function ReadingReview() {
  const { readingId } = useParams({ strict: false }) as { readingId: string };
  const { items, intent, done } = useInbox();
  const review = useQuery(q.readingReview(readingId));
  const item = items.find((entry) => entry.kind === "reading" && entry.id === readingId);
  const [value, setValue] = useState<C.ReadingValueT | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setValue(null); setNote(""); setError(undefined); }, [readingId]);
  useEffect(() => {
    if (!review.data) return;
    // Start from the agent's draft when the rules allow it, else from what the rules suggest.
    const allowed = (v: C.ReadingValueT | null | undefined) => v && review.data.allowed.some((a) => a.value === v && !a.refusal) ? v : null;
    setValue((current) => current ?? allowed(review.data.draft?.value) ?? allowed(review.data.suggestion.value));
    if (intent === "approve") confirmRef.current?.focus();
  }, [intent, review.data, readingId]);

  if (review.isLoading) return <DetailPanelShell><Skeleton shape="panel" /></DetailPanelShell>;
  if (!review.data) return <DetailPanelShell><ErrorText error={review.error} /></DetailPanelShell>;
  const data = review.data;
  const mode = data.measurement.mode;
  const open = (data.action.status === "reading_due" || data.action.status === "live") && !data.reading;
  const confirm = async () => {
    if (!value) return;
    setBusy(true); setError(undefined);
    try {
      await api.post(`/api/readings/${encodeURIComponent(data.action.id)}/confirm`, { value, ...(note.trim() ? { note: note.trim() } : {}) });
      done(I.readingConfirmed);
    } catch (cause) { setError(cause); }
    finally { setBusy(false); }
  };
  return <Review label={I.detail} title={data.action.title}
    meta={<><Pill tone={open ? "orange" : "gray"} status>{open ? I.reason[data.action.status === "reading_due" ? "reading_due" : "reading_drafted"] : I.confirmedReading}</Pill>{item && <ProductLabel product={item.product} />}</>}
    actions={open && <Button ref={confirmRef} tone="primary" busy={busy} disabled={!value} onClick={() => void confirm()}>{I.confirmReading} <Kbd>A</Kbd></Button>}>
    <p className="z-small z-muted">{I.readingNote}</p>
    <dl className="z-props">
      <dt>{I.where}</dt><dd>{I.actionKind[data.action.kind] ?? data.action.kind} · <ChannelName channel={data.action.channel} />{data.action.attribution_unit && <> · <span className="z-mono">{data.action.attribution_unit}</span></>}</dd>
      {data.action.playbook && <><dt>{I.playbook}</dt><dd className="z-mono">{I.playbookVersion(data.action.playbook.id, data.action.playbook.version)}</dd></>}
      <dt>{I.measurement}</dt><dd>{I.mode[mode] ?? mode}</dd>
      <dt>{I.window}</dt><dd>{I.phase[data.measurement.phase] ?? data.measurement.phase}{data.measurement.matures_at && data.measurement.phase !== "matured" && data.measurement.phase !== "closed" && <> · {I.maturesAt(data.measurement.matures_at)}</>}</dd>
      {data.measurement.acceptance.length > 0 && <><dt>{I.acceptance}</dt><dd><PointList items={data.measurement.acceptance} /></dd></>}
      {data.receipt && <><dt>{I.delivery}</dt><dd>{data.receipt.url ? <a href={data.receipt.url} target="_blank" rel="noreferrer noopener">{data.receipt.url}</a> : I.actionKind[data.action.kind] ?? data.action.kind} · {I.verification[data.receipt.verification] ?? data.receipt.verification}</dd></>}
      {data.post && <><dt><ChannelName channel={data.action.channel} /></dt><dd>{data.post.removed ? I.postRemoved : I.postStats(number(data.post.replies), number(data.post.score))}</dd></>}
    </dl>
    {data.metric_measurement && <section className="z-flow"><h3>{I.metrics}</h3><MetricSummary result={data.metric_measurement} /></section>}
    <section className="z-flow">
      <h3>{I.rulesSuggest}</h3>
      <p>{data.suggestion.value ? <Pill tone={READING[data.suggestion.value].tone} status>{readingLabel(data.suggestion.value, mode)}</Pill> : I.noSuggestion}</p>
      <Reasons reasons={data.suggestion.reasons} />
    </section>
    {data.draft && <section className="z-flow">
      <h3>{I.agentDraft}</h3>
      <p className="z-small z-muted">{I.agentDraftNote}</p>
      <p><Pill tone={READING[data.draft.value].tone} status>{readingLabel(data.draft.value, mode)}</Pill></p>
      <Excerpt className="z-agent-text" lines={3}>{data.draft.explanation}</Excerpt>
    </section>}
    {data.reading && <dl className="z-props"><dt>{I.confirmedReading}</dt><dd>{readingLabel(data.reading.value, mode)}{data.reading.note && <> · <span className="z-agent-text">{data.reading.note}</span></>}</dd></dl>}
    {open && <div className="z-flow" role="radiogroup" aria-labelledby="your-reading">
      <h3 id="your-reading">{I.yourReading}</h3>
      <ul className="z-check">
        {data.allowed.map((option) => <li key={option.value}>
          <input id={`reading-${option.value}`} type="radio" name="reading-value" value={option.value} checked={value === option.value} disabled={busy || option.refusal !== null} onChange={() => setValue(option.value)} />
          <label htmlFor={`reading-${option.value}`}>{readingLabel(option.value, mode)}{option.refusal && <span className="z-small z-muted"> · {reasonText({ code: option.refusal })}</span>}</label>
        </li>)}
      </ul>
      <div className="z-field">
        <label htmlFor="reading-note">{I.readingNoteLabel}</label>
        <textarea id="reading-note" className="z-input" rows={2} maxLength={400} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} />
      </div>
    </div>}
    <ErrorText error={error} />
  </Review>;
}
